'use client';

import { get, kebabCase } from 'lodash-es';
import getQualifiedImageUrl from '../../utils/imageUrlProcessor';
import Typography from '@ui/Typography';
import useShareModalStore from '@/components/store/shareModalSlice';
import Button from '@ui/Button';
import Input from '@ui/Input';
import React, { useState, useEffect } from 'react';
import { MdContentCopy, MdDone } from 'react-icons/md';
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share';
import { useBreakpointHooks } from '@/utils/break-points.provider';
import { useCreatePostsStore } from '@/utils/CreatePostDialogs';
import { getPostLink } from '@/utils/post.helper';
import { usePathname, useRouter } from 'next/navigation';
import { getEnvBaseUrl, joinUrl } from '@/utils/stringHelpers';
import useUserProfile from '@/hooks/useUserProfile';

const iconSize = 65;
const smallIconSize = 40;
const windowSize = {
  width: 1024,
  height: 768,
};

function ActivitySuccessContent(props) {
  const {
    post = null,
    reward = null,
    donate = false,
    message = null,
    isPost = false,
    nonProfit = {},
    donateType = null,
    isPostPublic = false,
    onContinue = () => {},
    hasUserSession = false,
    donationMessageTemplates,
  } = props;

  const badge = get(reward, 'badges.badge', null);
  const pointsEarned = get(reward, 'points.earned', 0);

  const user = useUserProfile();
  const router = useRouter();
  const nekiBaseUrl = getEnvBaseUrl();

  const pathName = usePathname();

  const postLink = !donate && post ? getPostLink(post) : null;

  const metadata = useShareModalStore((state) => state.metadata);

  const createPostShareLink = (url) => {
    let shareUrl = url || `${window.location.origin}${pathName}`;
    if (!shareUrl.startsWith(window.location.origin)) {
      shareUrl = joinUrl(window.location.origin, url);
    }
    return shareUrl;
  };

  const createDonationShareLink = () => {
    // TODO: check
    // let url = `nonprofit/${kebabCase(nonProfit?.name)}-${nonProfit?.id}`;
    let url = `nonprofit/${kebabCase(nonProfit?.name)}`;
    if (nonProfit?.company?.publicProfile?.enabled) {
      url = nonProfit?.company?.publicProfile?.slug;
    }
    let shareUrl = url || `${window?.location?.origin}${pathName}`;
    if (!shareUrl.startsWith(window?.location?.origin)) {
      shareUrl = joinUrl(
        window?.location?.origin || nekiBaseUrl,
        url,
      );
    }
    return shareUrl;
  };

  const shareUrl = postLink
    ? createPostShareLink(postLink)
    : createDonationShareLink();
  const title = metadata.title || 'Neki';
  const randomMessage = donationMessageTemplates[
    Math.floor(Math.random() * donationMessageTemplates.length)
  ].replace(/\[Nonprofit Name\]/g, `${nonProfit?.name}`);
  // const description = donate
  //   ? `Just ${donateType === 'gift-of-giving' ? 'contributed' : 'donated'} to ${
  //       nonProfit?.name
  //     } on Neki — let's inspire change together!`
  //   : `Just shared my story on Neki-join the movement of change!`;
  const description = randomMessage;

  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 600);
    }
  }, [copied]);

  const handleLinkCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
  };

  const isLessThanLargeScreen = useBreakpointHooks((bp) => bp.useSmaller('lg'));
  const isLessThanMediumScreen = useBreakpointHooks((bp) =>
    bp.useSmaller('md'),
  );
  const isGreaterThanMediumScreen = useBreakpointHooks((bp) =>
    bp.useGreater('md'),
  );

  const { openDialog } = useCreatePostsStore();

  const handleCreatePost = () => {
    onContinue();
    setTimeout(() => {
      openDialog('UserPost', description, nonProfit);
    }, 100);
  };

  const handleRedirect = () => {
    // TODO: check
    // let url = `nonprofit/${kebabCase(nonProfit?.name)}-${nonProfit?.id}`;
    let url = `nonprofit/${kebabCase(nonProfit?.name)}`;
    if (nonProfit?.company?.publicProfile?.enabled) {
      url = nonProfit?.company?.publicProfile?.slug;
    }
    router.push(`/${url || 'nonprofit'}`);
  };

  if (isPost && !isPostPublic) {
    return (
      <div className="mt-4 flex flex-col items-center">
        <div className="left flex-1/2 flex flex-col items-center justify-center gap-3">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={
              badge
                ? getQualifiedImageUrl(badge?.imageUrl)
                : '/assets/images/star.png'
            }
            alt="badge"
            className="h-24 w-24"
          />
          {isPost === 'volunteer-application' ? (
            <>
              <Typography
                component="div"
                className="text-center text-32"
                variant="h1">
                Thank You!
              </Typography>
              <Typography
                component="div"
                className="mx-[70px] text-center font-medium"
                variant="h4">
                You have successfully applied as a volunteer. Please wait for
                the admin&apos;s approval.
              </Typography>
            </>
          ) : (
            <>
              {' '}
              <Typography
                component="div"
                className="text-center text-32"
                variant="h1">
                Congratulations!
              </Typography>
              {pointsEarned > 0 && (
                <Typography
                  component="div"
                  className="mx-[70px] text-center font-medium"
                  variant="h4">
                  You have earned <b>{pointsEarned} Nekies</b>{' '}
                  {badge && (
                    <>
                      and a <b>{badge.name} Badge</b>
                    </>
                  )}
                </Typography>
              )}
            </>
          )}
        </div>
        <Typography
          component="div"
          variant="hyperlink"
          className="mt-4 cursor-pointer text-center text-15"
          onClick={onContinue}>
          <u>Continue</u>
        </Typography>
      </div>
    );
  }

  if (donate && hasUserSession) {
    return (
      <div className="mt-4 flex flex-col items-center">
        <div
          className={`left flex-1/2 flex flex-col items-center justify-center gap-3 ${
            isGreaterThanMediumScreen ? 'mx-[150px]' : ''
          }`}>
          <img
            src={'/assets/images/neki-heart.png'}
            alt="donation-heart"
            className="h-24 w-24"
          />
          <Typography
            component="div"
            className="text-center text-32"
            variant="h1">
            {user?.first_name || user?.firstName || 'User'}, Thank you so much!
          </Typography>
          {/* // TODO: check shyam */}
          {message ? (
            <>
              <Typography
                component="div"
                color="textDisabled"
                className="mx-[70px] text-center font-medium"
                variant="h4">
                {message}
              </Typography>
              {/* <Typography
                component="div"
                className="mx-[70px] text-center font-medium"
                variant="h4">
                Don&apos;t be shy! Share your donation on Neki and encourage
                others to participate in your community!
              </Typography>
              <Button className="" onClick={handleCreatePost}>
                Create a Post and Share
              </Button> */}
            </>
          ) : (
            <>
              {' '}
              <Typography
                component="div"
                color="textDisabled"
                className="mx-[70px] text-center font-medium"
                variant="h4">
                {donateType === 'gift-of-giving'
                  ? 'Your gift has been processed successfully. A confirmation of your contribution will be sent to your email.'
                  : 'Your donation has been processed successfully. The receipt will be sent to your email.'}
              </Typography>
              <Typography
                component="div"
                className="mx-[70px] text-center font-medium"
                variant="h4">
                Don&apos;t be shy! Share your{' '}
                {donateType === 'gift-of-giving' ? 'contribution' : 'donation'}{' '}
                on Neki and encourage others to participate in your community!
              </Typography>
              <Button onClick={handleCreatePost}>
                Create a Post and Share
              </Button>
            </>
          )}
        </div>
        <Typography
          component="div"
          variant="hyperlink"
          className="mb-4 mt-4 cursor-pointer text-center text-15"
          onClick={onContinue}>
          <u>{message ? 'Continue' : 'or Continue...'}</u>
        </Typography>
      </div>
    );
  }

  return (
    <div className="mt-4 flex flex-col items-center">
      <div
        className={`flex w-full flex-1 ${
          isLessThanLargeScreen
            ? 'flex-col justify-center divide-y'
            : 'items-center'
        }`}>
        {donate ? (
          <div className="flex-1/2 flex flex-col items-center justify-center gap-3">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={'/assets/images/neki-heart.png'}
              alt="donation-heart"
              className="h-24 w-24"
            />
            <Typography
              component="div"
              className="text-center text-32"
              variant="h1">
              {user?.first_name || user?.firstName || ''}, Thank you so much!
            </Typography>
            <Typography
              component="div"
              color="textDisabled"
              className="mx-[70px] text-center font-medium"
              variant="h4">
              Your donation has been processed successfully. The receipt will be
              sent to your email.
            </Typography>
          </div>
        ) : (
          <div className="flex-1/2 flex flex-col items-center justify-center gap-3">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={
                badge
                  ? getQualifiedImageUrl(badge?.imageUrl)
                  : '/assets/images/star.png'
              }
              alt="badge"
              className="h-24 w-24"
            />
            <Typography
              component="div"
              className="text-center text-32"
              variant="h1">
              Congratulations!
            </Typography>
            {pointsEarned > 0 && (
              <Typography
                component="div"
                className="mx-[70px] text-center font-medium"
                variant="h4">
                You have earned <b>{pointsEarned} Nekies</b>{' '}
                {badge && (
                  <>
                    and a <b>{badge.name} Badge</b>
                  </>
                )}
              </Typography>
            )}
          </div>
        )}
        <div
          className={`flex-1/2 flex flex-col items-center justify-center ${
            donate ? 'gap-10' : 'gap-12'
          } ${isLessThanLargeScreen ? 'mt-6' : ''}`}>
          <Typography
            component="div"
            className={`text-center font-medium ${
              isLessThanLargeScreen ? 'mt-6' : ''
            }`}
            variant="h4">
            {donate
              ? `Share your donation on social media and inspire others to participate in your cause!`
              : `Don't be shy! Share your badge and encourage others to
            participate in your community!`}
          </Typography>
          <div
            className={`flex flex-wrap items-center justify-start ${
              isLessThanMediumScreen ? 'gap-4' : 'gap-6'
            }`}>
            <EmailShareButton
              url={shareUrl}
              subject={title}
              body={`${description} \n\n`}>
              <EmailIcon
                size={isLessThanMediumScreen ? smallIconSize : iconSize}
                windowWidth={windowSize.width}
                windowHeight={windowSize.height}
                round
              />
            </EmailShareButton>

            <WhatsappShareButton
              url={shareUrl}
              title={title}
              separator=" "
              windowWidth={windowSize.width}
              windowHeight={windowSize.height}
              //   onClick={handleClose}
            >
              <WhatsappIcon
                size={isLessThanMediumScreen ? smallIconSize : iconSize}
                round
              />
            </WhatsappShareButton>

            <FacebookShareButton
              url={shareUrl}
              quote={title}
              windowWidth={windowSize.width}
              windowHeight={windowSize.height}
              //   onClick={handleClose}
            >
              <FacebookIcon
                size={isLessThanMediumScreen ? smallIconSize : iconSize}
                round
              />
            </FacebookShareButton>

            <TwitterShareButton
              url={shareUrl}
              title={title}
              windowWidth={windowSize.width}
              windowHeight={windowSize.height}
              //   onClick={handleClose}
            >
              <TwitterIcon
                size={isLessThanMediumScreen ? smallIconSize : iconSize}
                round
              />
            </TwitterShareButton>

            <LinkedinShareButton
              url={shareUrl}
              title={encodeURIComponent(title)}
              summary={encodeURIComponent(description)}
              source="Neki"
              windowWidth={windowSize.width}
              windowHeight={windowSize.height}
              //   onClick={handleClose}
            >
              <LinkedinIcon
                size={isLessThanMediumScreen ? smallIconSize : iconSize}
                round
              />
            </LinkedinShareButton>
          </div>

          <div className="flex w-full items-center justify-between gap-6">
            <div className="flex-1">
              <Input value={shareUrl} disabled />
            </div>
            <Button variant="filled" color="primary" onClick={handleLinkCopy}>
              {!copied ? (
                <MdContentCopy />
              ) : (
                <MdDone className="animate-pulse" />
              )}
            </Button>
          </div>
          {/* <Button className="w-full">Create a Post and Share</Button> */}
        </div>
      </div>
      <Typography
        component="div"
        variant="hyperlink"
        className="mt-4 cursor-pointer text-center text-15"
        onClick={hasUserSession ? onContinue : handleRedirect}>
        <u>or Continue...</u>
      </Typography>
    </div>
  );
}

export default ActivitySuccessContent;
